/**
 * @name Settings
 * @description Configuración de perfil del usuario
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import { Layout, MUI, Typography } from '@tycommon/styles';
import { BackupService } from '@services';

// Components
import {
  ChangePassword,
  PersonalData,
  Responsibilities,
  ResponsibilitiesProgress,
  Schedule,
  PendingSchedule,
  Backup,
  BackupAssignments,
  JobChange,
  PendingJobChange,
  BackupTabs
} from './components';

const a11yProps = (index) => {
  return {
    id: `scrollable-auto-tab-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-${index}`,
  };
};

const TabPanel = (props) => {
  const { active, children } = props;
  return ((active) && (<div role="tabpanel">{children}</div>));
};

TabPanel.propTypes = {
  active: PropTypes.any.isRequired,
  children: PropTypes.node.isRequired
};

class ProfileSettings extends Component {
  constructor(props) {
    super(props);
    let URLParams = props.location.search;
    this.queryParams = new URLSearchParams(URLParams);
    const infoUsuario = JSON.parse(localStorage.getItem('ls.infoUsuario') || '{}');
    this.enterpriseParams = infoUsuario && infoUsuario.Empresa ? infoUsuario.Empresa : null;
    this.userInfo = infoUsuario && infoUsuario.Empleado ? infoUsuario.Empleado : null;
    if (!this.enterpriseParams || !this.userInfo) {
      window.location.href = '/login';
      return;
    }
    this.state = {
      isThirdLevel: this.userInfo.NivelJerarquia === 3,
      isSupervisor: this.userInfo.NivelJerarquia === 2,
      showResponsibilities: this.enterpriseParams.MostrarResponsabilidades,
      showResponsibilitiesFirst: this.queryParams.get('idEstatus') ? this.queryParams.get('idEstatus') : false,
      selectedTab: 0,
      hasResponsabilities: false
    };
  }

  changeTab = (tab) => {
    this.setState({ selectedTab: tab });
  }

  changeIndexTab = (index) => {
    this.setState({ selectedTab: index });
  }

  componentDidMount() {
    const { showResponsibilitiesFirst } = this.state;

    if (showResponsibilitiesFirst) {
      this.setState({ selectedTab: 2 });
    }

    // LocalStorage - Notificación
    if (localStorage.getItem('ls.Parametros')) {
      let params = JSON.parse(localStorage.getItem('ls.Parametros'));
      localStorage.removeItem('ls.Parametros');
      this.setState({ selectedTab: params.Tab ? params.Tab : 0 });
    }

    this.getResponsabilities(this.userInfo.IdEmpleado);
  }

  // Consulta todas las responsabilidades
  getResponsabilities = (idEmployee) => {
    BackupService.getResponsabilieties(idEmployee)
      .then((response) => {
        this.setState({ hasResponsabilities: true });
      })
      .catch(() => { });
  }


  render() {
    const {
      isSupervisor,
      isThirdLevel,
      selectedTab,
      showResponsibilities,
      hasResponsabilities
    } = this.state;

    return (
      <>
        <Layout.Title>
          <Typography.H3>
            {i18n.t('Settings.profileConfig')}
          </Typography.H3>
        </Layout.Title>

        <Layout.Content>
          <Styled.TabsWrapper>
            <MUI.Tabs
              value={selectedTab}
              onChange={(event, tab) => this.changeTab(tab)}
            >
              {/* Datos personales */}
              <MUI.Tab value={0} label={i18n.t('Settings.personalData')} {...a11yProps(0)} />
              {/* Cambiar constraseña */}
              <MUI.Tab value={1} label={i18n.t('ChangePass.changePass')} {...a11yProps(1)} />
              {/* Responsabilidades */}
              {(showResponsibilities) && (
                <MUI.Tab value={2} label={i18n.t('GenericMessages.responsibilities')} {...a11yProps(2)} />
              )}
              {/* Avance de responsabilidades */}
              {(showResponsibilities && (isSupervisor || isThirdLevel)) && (
                <MUI.Tab value={3} wrapped label={i18n.t('GenericMessages.respProgressTab')} {...a11yProps(3)} />
              )}
              {/* Configuración de horario */}
              <MUI.Tab value={4} wrapped label={i18n.t('Shedule.scheduleSetting')} {...a11yProps(4)} />
              {/* Revisión de horario */}
              {(isSupervisor || isThirdLevel) && (
                <MUI.Tab value={5} wrapped label={i18n.t('Shedule.scheduleReview')} {...a11yProps(5)} />
              )}
              {/* Asignar backup */}
              {hasResponsabilities && (
                <MUI.Tab value={6} wrapped label={i18n.t('Backup')} {...a11yProps(6)} />
              )}
              {/* Cambio de puesto */}
              <MUI.Tab value={7} wrapped label={i18n.t('Request.jobChange')} {...a11yProps(7)} />
              {/* Revisión de cambio de puesto */}
              {(isSupervisor || isThirdLevel) && (
                <MUI.Tab value={8} wrapped label={i18n.t('Request.requestReview')} {...a11yProps(8)} />
              )}
            </MUI.Tabs>

            <MUI.TabsSwipeable
              index={selectedTab}
              onChangeIndex={this.changeIndexTab}
              slideStyle={{ overflow: 'visible' }}
            >
              {/* Datos personales */}
              <TabPanel active={selectedTab === 0} index={0}>
                <Styled.TabContent>
                  <PersonalData />
                </Styled.TabContent>
              </TabPanel>

              {/* Cambiar constraseña */}
              <TabPanel active={selectedTab === 1} index={1}>
                <Styled.TabContent>
                  <ChangePassword />
                </Styled.TabContent>
              </TabPanel>

              {/* Responsabilidades */}
              {(showResponsibilities) && (
                <TabPanel active={selectedTab === 2} index={2}>
                  <Styled.TabContent>
                    <Responsibilities
                      queryParams={this.queryParams}
                      onChangeTab={this.changeTab}
                    />
                  </Styled.TabContent>
                </TabPanel>
              )}

              {/* Avance de responsabilidades */}
              {(showResponsibilities && (isSupervisor || isThirdLevel)) && (
                <TabPanel active={selectedTab === 3} index={3}>
                  <Styled.TabContent>
                    <ResponsibilitiesProgress />
                  </Styled.TabContent>
                </TabPanel>
              )}

              {/* Configuración de horario */}
              <TabPanel active={selectedTab === 4} index={4}>
                <Styled.TabContent>
                  <Schedule />
                </Styled.TabContent>
              </TabPanel>

              {/* Revisión de horario */}
              {(isSupervisor || isThirdLevel) && (
                <TabPanel active={selectedTab === 5} index={5}>
                  <Styled.TabContent>
                    <PendingSchedule
                      queryParams={this.queryParams}
                      onChangeTab={this.changeTab}
                    />
                  </Styled.TabContent>
                </TabPanel>
              )}

              {/* Asignar backup */}
              {hasResponsabilities && (
                <TabPanel active={selectedTab === 6} index={6}>
                  <Styled.TabContent>
                    <BackupTabs />
                  </Styled.TabContent>
                </TabPanel>
              )}

              {/* Cambio de puesto */}
              <TabPanel active={selectedTab === 7} index={7}>
                <Styled.TabContent>
                  <JobChange />
                </Styled.TabContent>
              </TabPanel>

              {/* Revisión de cambio de puesto */}
              {(isSupervisor || isThirdLevel) && (
                <TabPanel active={selectedTab === 8} index={8}>
                  <PendingJobChange
                    queryParams={this.queryParams}
                    onChangeTab={this.changeTab}
                  />
                </TabPanel>
              )}
            </MUI.TabsSwipeable>
          </Styled.TabsWrapper>
        </Layout.Content>
      </>
    );
  }
}

ProfileSettings.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.TabsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 500px;
`;

Styled.TabContent = styled.div`
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
`;

export default ProfileSettings;
